"use client"

import { useEffect, useRef } from "react"
import { useSearchParams } from "next/navigation"
import { track } from "@/shared/lib/analytics"
import {
  assignCohort,
  browserLocalStorage,
  buildEligibleEvent,
  checkoutJustStarted,
  clearPending,
  evaluateCancelReturn,
  evaluateDelayedAbandon,
  evaluatePaywallClose,
  hasSeen,
  markSeen,
  readPending,
  subjectKey,
  utcDay,
  type Branch,
} from "@/shared/lib/sales-closer/hook"
import { useUIStore } from "@/shared/stores/ui-store"

function emitEligible(userId: string, branch: Branch, triggerId: string): void {
  const storage = browserLocalStorage()
  const key = `${subjectKey(userId)}:${branch}:${triggerId}`
  if (hasSeen(storage, key)) return
  const event = buildEligibleEvent(branch, assignCohort(userId))
  markSeen(storage, key)
  track(event.event, event.properties)
}

/**
 * Records sales_closer_eligible. Does not mount a thread or send a message.
 */
export function SalesCloserHook({
  userId,
  plan,
  signedIn,
  ready,
}: {
  userId: string | null
  plan: string | null
  signedIn: boolean
  ready: boolean
}) {
  const activeModal = useUIStore((state) => state.activeModal)
  const paywallTrigger = useUIStore((state) => state.paywallContext?.trigger ?? null)
  const searchParams = useSearchParams()
  const openTrigger = useRef<string | null>(null)
  const openUserId = useRef<string | null>(null)
  const wasOpen = useRef(false)

  useEffect(() => {
    if (activeModal !== "subscribe-paywall") return
    if (!wasOpen.current) openUserId.current = signedIn ? userId : null
    wasOpen.current = true
    if (paywallTrigger) openTrigger.current = paywallTrigger
  }, [activeModal, paywallTrigger, signedIn, userId])

  useEffect(() => {
    if (activeModal === "subscribe-paywall") return
    if (!wasOpen.current) return
    // Consume the open state on every close, even when signed out or not ready,
    // so a close from one auth state is never recorded under a later one.
    wasOpen.current = false
    const trigger = openTrigger.current
    const opener = openUserId.current
    openTrigger.current = null
    openUserId.current = null
    if (!signedIn || !userId) return
    const now = Date.now()
    const branch = evaluatePaywallClose({
      ready,
      signedIn,
      userId,
      openerUserId: opener,
      plan,
      trigger,
      checkoutStarted: checkoutJustStarted(readPending(browserLocalStorage(), userId), now),
    })
    if (!branch) return
    emitEligible(userId, branch, `free:${utcDay(now)}`)
  }, [activeModal, plan, ready, signedIn, userId])

  useEffect(() => {
    if (!ready || !signedIn || !userId) return
    const storage = browserLocalStorage()
    const canceled =
      searchParams.get("canceled") === "true" ||
      searchParams.get("cancelled") === "true"
    const paidReturn =
      searchParams.get("success") === "true" || searchParams.has("session_id")
    if (paidReturn || plan !== "free") {
      clearPending(storage, userId)
      return
    }
    const now = Date.now()
    const cancelBranch = evaluateCancelReturn({
      signedIn,
      plan,
      canceled,
      hasPlanParam: Boolean(searchParams.get("tier") || searchParams.get("billing")),
    })
    if (cancelBranch) {
      const attempt = searchParams.get("checkout_attempt_id") || "cancel"
      emitEligible(userId, cancelBranch, `cancel:${attempt}`)
      clearPending(storage, userId)
      return
    }
    const pending = readPending(storage, userId)
    const delayed = evaluateDelayedAbandon({
      signedIn,
      plan,
      startedAt: pending?.startedAt ?? null,
      now,
      paymentArrived: plan !== "free",
    })
    if (!delayed || !pending) return
    emitEligible(userId, delayed, `delay:${pending.attemptId}:${pending.startedAt}`)
    clearPending(storage, userId)
  }, [plan, ready, searchParams, signedIn, userId])

  return null
}
